<template>
  <section
    v-if="hasTimelineAccess"
    class="irp-recent-activity-panel"
    data-testid="irp-recent-activity-panel"
  >
    <h2 class="irp-recent-activity-panel__title">Recent Activity</h2>
    <div
      v-if="isLoading"
      class="irp-recent-activity-panel__skeletons"
      data-testid="irp-recent-activity-skeletons"
    >
      <div
        v-for="skeleton in SKELETON_COUNT"
        :key="skeleton"
        class="irp-recent-activity-panel__skeleton"
      >
        <BittsBone type="avatar" size="x-small" />
        <div class="irp-recent-activity-panel__skeleton-lines">
          <BittsBone type="heading" class="w-1/3" />
          <BittsBone type="copy" />
        </div>
      </div>
    </div>
    <BittsEmptyState
      v-else-if="hasError"
      :font-awesome-icon="ERROR_STATE.icon"
      :title="ERROR_STATE.title"
      :description="ERROR_STATE.description"
      :button="RETRY_BUTTON"
      data-testid="irp-recent-activity-error-state"
      @button-clicked="refetch"
    />
    <BittsEmptyState
      v-else-if="!recentEvents.length"
      :font-awesome-icon="EMPTY_STATE.icon"
      :title="EMPTY_STATE.title"
      :description="EMPTY_STATE.description"
      data-testid="irp-recent-activity-empty-state"
    />
    <BittsCard
      v-else
      class="irp-recent-activity-panel__card"
      :no-padding="false"
    >
      <div
        class="irp-recent-activity-panel__body ph-no-capture dd-privacy-mask"
      >
        <IRPRecentActivityEvent
          v-for="event in recentEvents"
          :key="event.uuid"
          :event="event"
          :account-name="individualRecordStore.recordName"
          class="irp-recent-activity-panel__event"
        />
      </div>
      <RouterLink
        :to="{ name: IRP_TAB_ROUTES[IRP_TAB.timeline], params: recordParams }"
      >
        <BittsButton
          text="View activity"
          type="neutral"
          variant="outline"
          size="x-small"
          data-testid="irp-recent-activity-view-all"
        />
      </RouterLink>
    </BittsCard>
  </section>
</template>

<script setup lang="ts">
import {
  BittsBone,
  BittsButton,
  BittsCard,
  BittsEmptyState,
} from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import { computed, watch } from 'vue';

import useAuth from '@/composables/useAuth';
import useReportTimelineEvents from '@/composables/useReportTimelineEvents';
import { ATTRIBUTION_EVENT_TYPES } from '@/constants/attribution';
import { defaultErrorMessage } from '@/errors';
import { useIndividualRecordStore, usePartnersStore } from '@/stores';
import type { TimelineEvent } from '@/types/timeline';

import { IRP_TAB, IRP_TAB_ROUTES } from './irp';
import IRPRecentActivityEvent from './IRPRecentActivityEvent.vue';
import { useRecordRouteParams } from './useRecordRouteParams';

const EVENT_COUNT = 4;
const SKELETON_COUNT = 3;

const MOVEMENT_EVENT_TYPES: string[] = [
  ATTRIBUTION_EVENT_TYPES.OVERLAP_ENTRY,
  ATTRIBUTION_EVENT_TYPES.OVERLAP_EXIT,
  ATTRIBUTION_EVENT_TYPES.POPULATION_ENTRY,
  ATTRIBUTION_EVENT_TYPES.POPULATION_EXIT,
];

const EMPTY_STATE: { title: string; description: string; icon: BittsIcon } = {
  title: 'No activity yet',
  description:
    'Activity between you and your partners on this account will appear here.',
  icon: ['fak', 'time'],
};

const ERROR_STATE: { title: string; description: string; icon: BittsIcon } = {
  title: 'Recent activity could not load',
  description: defaultErrorMessage,
  icon: ['fak', 'warning'],
};

const RETRY_BUTTON = {
  text: 'Try again',
  type: 'neutral',
  variant: 'outline',
} as const;

const recordParams = useRecordRouteParams();

const individualRecordStore = useIndividualRecordStore();
const partnersStore = usePartnersStore();
const { isReportEventAllowed } = useReportTimelineEvents();
const { hasScope } = useAuth();

const hasTimelineAccess = computed(() => hasScope('read:activity-timeline'));

const timeline = computed(() => individualRecordStore.timeline);

const isAwaitingRecordName = computed(
  () =>
    individualRecordStore.isRecordLoading && !individualRecordStore.recordName,
);

const isLoading = computed(
  () => timeline.value.isLoading || isAwaitingRecordName.value,
);
const hasError = computed(() => !!timeline.value.error);

const visibleEvents = computed(() =>
  timeline.value.items.filter(
    (event) => isReportEventAllowed(event) && hasKnownPartner(event),
  ),
);

const recentEvents = computed(() => {
  const notableEvents = visibleEvents.value.filter(
    (event) => !isMovementEvent(event),
  );
  const movementEvents = visibleEvents.value.filter(isMovementEvent);
  return [...notableEvents, ...movementEvents].slice(0, EVENT_COUNT);
});

function isMovementEvent(event: TimelineEvent) {
  return MOVEMENT_EVENT_TYPES.includes(event.type);
}

function hasKnownPartner(event: TimelineEvent) {
  if (!event.partner_organization_uuid) return true;
  return !!partnersStore.getPartnerOrgByUuid(event.partner_organization_uuid);
}

function refetch() {
  if (!hasTimelineAccess.value) return;
  return individualRecordStore.fetchTimeline();
}

watch(
  () =>
    [
      individualRecordStore.sourceId,
      individualRecordStore.sourcePrimaryKey,
    ] as const,
  refetch,
  { immediate: true },
);
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-recent-activity-panel {
  @apply flex flex-col gap-8;
}

.irp-recent-activity-panel__title {
  @apply text-lg font-bold text-neutral-text-strong;
}

.irp-recent-activity-panel__card {
  @apply flex flex-col justify-between gap-16 p-24;
}

.irp-recent-activity-panel__body {
  @apply flex flex-col gap-16;
}

.irp-recent-activity-panel__event {
  @apply border-b border-neutral-border pb-16;

  &:last-child {
    @apply border-b-0;
  }
}

.irp-recent-activity-panel__skeletons {
  @apply flex flex-col gap-16 rounded-bts-lg border border-neutral-border p-24;
}

.irp-recent-activity-panel__skeleton {
  @apply flex items-start gap-8;
}

.irp-recent-activity-panel__skeleton-lines {
  @apply flex flex-col gap-4 flex-1;
}
</style>
